<template>
  <v-container fluid class="pt-6">
    <v-row justify="center">
      <v-col cols="12" xl="10" xxl="8">
        <div class="page-section">
          <BackButton />
        </div>

        <section class="dashboard-shell" :class="{ 'is-compact': isCompact }">
          <aside v-if="!isCompact" class="dashboard-sidebar">
            <div class="sidebar-header">
              <div class="sidebar-eyebrow">Select</div>
              <h2 class="sidebar-title">Dashboards</h2>
            </div>

            <div v-if="isDashboardsLoading" class="sidebar-list">
              <div v-for="n in 4" :key="n" class="dashboard-item-skeleton" />
            </div>

            <div v-else-if="dashboards.length" class="sidebar-list">
              <button v-for="dashboard in dashboards" :key="dashboard.id" type="button"
                class="dashboard-item" :class="{ 'is-selected': dashboard.id === selectedDashboard?.id }"
                @click="onDashboardSelected(dashboard)">
                <v-icon size="18" color="#64ffda">mdi-view-dashboard-outline</v-icon>
                <span class="dashboard-item-label">{{ dashboard.name }}</span>
              </button>
            </div>

            <div v-else class="sidebar-empty">
              <v-icon size="32" color="#4a5a60">mdi-view-dashboard-outline</v-icon>
              <p>No dashboards available</p>
            </div>
          </aside>

          <main class="dashboard-main">
            <nav v-if="isCompact" class="chip-strip" aria-label="Dashboards">
              <template v-if="isDashboardsLoading">
                <div v-for="n in 3" :key="n" class="chip-skeleton" />
              </template>
              <template v-else>
                <button v-for="dashboard in dashboards" :key="dashboard.id" type="button"
                  class="dashboard-chip" :class="{ 'is-selected': dashboard.id === selectedDashboard?.id }"
                  @click="onDashboardSelected(dashboard)">
                  <v-icon size="16" color="#64ffda">mdi-view-dashboard-outline</v-icon>
                  <span>{{ dashboard.name }}</span>
                </button>
              </template>
            </nav>

            <div v-if="selectedDashboard" class="dashboard-content">
              <header class="dashboard-header">
                <div class="dashboard-eyebrow">Dashboard</div>
                <h1 class="dashboard-title">{{ selectedDashboard.name }}</h1>
              </header>

              <div class="grid-wrapper">
                <div class="grid-container" :class="useGridLayout ? 'grid-desktop' : 'grid-stacked'">
                  <component v-for="component in orderedComponents" :is="getDashboardComponent(component.type)"
                    :key="component.id" v-bind="component.properties" :title="component.title"
                    :dataFetcher="() => getDataFetcher(component.dataSource.serviceName, component.dataSource.serviceMethod, component.dataSource.parameters)"
                    :style="componentStyle(component)" />
                </div>
              </div>
            </div>

            <div v-else-if="isDashboardsLoading" class="dashboard-loading">
              <v-skeleton-loader type="heading, list-item-three-line, image" />
            </div>

            <div v-else-if="!dashboards.length" class="dashboard-empty">
              <v-icon size="56" color="#4a5a60">mdi-view-dashboard-outline</v-icon>
              <p class="empty-title">No dashboards available</p>
              <p class="empty-subtitle">There are no dashboards configured for this asset yet.</p>
            </div>

            <div v-else class="dashboard-empty">
              <v-icon size="56" color="#4a5a60">mdi-view-dashboard-outline</v-icon>
              <p class="empty-title">Select a dashboard</p>
              <p class="empty-subtitle">
                {{ isCompact ? 'Choose one above to begin.' : 'Choose one from the sidebar to begin.' }}
              </p>
            </div>
          </main>
        </section>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { useDisplay } from 'vuetify';
import BackButton from '@/components/buttons/BackButton.vue';
import DashboardService from '@/services/DashboardService';
import DashboardComponentFactory from '@/factories/DashboardComponentFactory';
import ServiceFactory from '@/factories/ServiceFactory';
import { useAssetStore } from '@/store/assetStore';

export default {
  name: 'DashboardView',
  components: {
    BackButton,
  },
  setup() {
    const { smAndDown, mdAndUp } = useDisplay();
    return { isCompact: smAndDown, useGridLayout: mdAndUp };
  },
  data() {
    return {
      selectedAsset: null,
      dashboards: [],
      selectedDashboard: null,
      assetStore: null,
      isDashboardsLoading: true,
    }
  },
  computed: {
    orderedComponents() {
      const components = this.selectedDashboard?.components || [];
      return [...components].sort((a, b) => {
        const ay = a.grid?.position?.y ?? 0;
        const by = b.grid?.position?.y ?? 0;
        if (ay !== by) return ay - by;
        return (a.grid?.position?.x ?? 0) - (b.grid?.position?.x ?? 0);
      });
    }
  },
  async mounted() {
    this.getAsset();
    try {
      this.dashboards = await DashboardService.getDashboardsByAssetId(this.selectedAsset.id);
      if (this.dashboards.length && !this.selectedDashboard) {
        this.selectedDashboard = this.dashboards[0];
      }
    } catch (error) {
      console.error("Failed to load dashboards", error);
    } finally {
      this.isDashboardsLoading = false;
    }
  },
  methods: {
    getAsset() {
      this.assetStore = useAssetStore();
      this.selectedAsset = this.assetStore.asset;
    },
    onDashboardSelected(dashboard) {
      this.selectedDashboard = dashboard;
    },
    componentStyle(component) {
      if (!this.useGridLayout) {
        return { gridColumn: '1 / -1', gridRow: 'auto' };
      }
      const sizeX = component.grid?.size?.x || 1;
      const sizeY = component.grid?.size?.y || 1;
      return {
        gridColumn: `${component.grid.position.x} / span ${sizeX}`,
        gridRow: `${component.grid.position.y} / span ${sizeY}`,
      };
    },
    async getDataFetcher(serviceName, methodName, rawParams) {
      const service = ServiceFactory.getService(serviceName);
      if (!service || typeof service[methodName] !== 'function') {
        throw new Error(`Service or method not found: ${serviceName}.${methodName}`);
      }

      const resolvedParams = [];
      for (const key in rawParams) {
        const value = rawParams[key];
        resolvedParams.push(typeof value === "function" ? await value() : value);
      }

      if (!Array.isArray(resolvedParams)) {
        throw new Error("Parameters must be converted to an array before spreading.");
      }

      return service[methodName](...resolvedParams);
    },
    getDashboardComponent(type) {
      return DashboardComponentFactory.getComponent(type);
    },
  }
}
</script>

<style scoped>
.page-section {
  margin-bottom: 24px;
}

.dashboard-shell {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 16px;
  align-items: stretch;
  min-height: calc(100vh - 220px);
}

.dashboard-shell.is-compact {
  grid-template-columns: 1fr;
  min-height: 0;
}

/* ----- Sidebar ----- */
.dashboard-sidebar {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  padding: 20px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-height: 0;
}

.sidebar-header {
  padding: 0 4px;
}

.sidebar-eyebrow {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8aa0aa;
  margin-bottom: 2px;
}

.sidebar-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  line-height: 1.2;
}

.sidebar-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
  padding-right: 4px;
}

.sidebar-list::-webkit-scrollbar {
  width: 6px;
}

.sidebar-list::-webkit-scrollbar-thumb {
  background: #3a4a52;
  border-radius: 3px;
}

.sidebar-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 8px;
  color: #8aa0aa;
  font-size: 0.85rem;
  text-align: center;
}

.dashboard-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid transparent;
  border-radius: 10px;
  color: #cfd8dc;
  font: inherit;
  font-size: 0.95rem;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.dashboard-item:hover {
  background: rgba(100, 255, 218, 0.06);
  color: #ffffff;
}

.dashboard-item.is-selected {
  background: rgba(100, 255, 218, 0.1);
  border-color: rgba(100, 255, 218, 0.3);
  color: #ffffff;
  font-weight: 600;
}

.dashboard-item-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dashboard-item-skeleton {
  height: 44px;
  border-radius: 10px;
  background: linear-gradient(90deg,
    rgba(255, 255, 255, 0.02) 0%,
    rgba(255, 255, 255, 0.05) 50%,
    rgba(255, 255, 255, 0.02) 100%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s ease-in-out infinite;
}

@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ----- Main panel ----- */
.dashboard-main {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  padding: 24px 26px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
}

@media (max-width: 600px) {
  .dashboard-main {
    padding: 16px 14px;
    border-radius: 12px;
  }
}

/* ----- Chip strip (compact viewports) ----- */
.chip-strip {
  display: flex;
  gap: 8px;
  margin-bottom: 18px;
  overflow-x: auto;
  scrollbar-width: none;
  padding-bottom: 4px;
  -webkit-overflow-scrolling: touch;
}

.chip-strip::-webkit-scrollbar {
  display: none;
}

.dashboard-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 999px;
  color: #cfd8dc;
  font: inherit;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.dashboard-chip:hover {
  background: rgba(100, 255, 218, 0.06);
  color: #ffffff;
}

.dashboard-chip.is-selected {
  background: rgba(100, 255, 218, 0.12);
  border-color: rgba(100, 255, 218, 0.4);
  color: #ffffff;
  font-weight: 600;
}

.chip-skeleton {
  flex: 0 0 auto;
  height: 32px;
  width: 110px;
  border-radius: 999px;
  background: linear-gradient(90deg,
    rgba(255, 255, 255, 0.02) 0%,
    rgba(255, 255, 255, 0.05) 50%,
    rgba(255, 255, 255, 0.02) 100%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s ease-in-out infinite;
}

/* ----- Dashboard content ----- */
.dashboard-content {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1 1 auto;
}

.dashboard-header {
  margin-bottom: 16px;
}

.dashboard-eyebrow {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8aa0aa;
  margin-bottom: 2px;
}

.dashboard-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  line-height: 1.2;
  word-break: break-word;
}

@media (max-width: 600px) {
  .dashboard-title {
    font-size: 1.2rem;
  }
}

.grid-wrapper {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
  padding-bottom: 8px;
}

.grid-wrapper::-webkit-scrollbar {
  width: 8px;
}

.grid-wrapper::-webkit-scrollbar-thumb {
  background: #3a4a52;
  border-radius: 4px;
}

.grid-container {
  display: grid;
  grid-auto-rows: minmax(100px, auto);
  gap: 10px;
}

.grid-container.grid-desktop {
  grid-template-columns: repeat(12, 1fr);
}

.grid-container.grid-stacked {
  grid-template-columns: 1fr;
  grid-auto-flow: row;
  grid-auto-rows: auto;
  gap: 14px;
}

/* ----- Loading / empty states ----- */
.dashboard-loading {
  padding: 4px;
}

.dashboard-loading :deep(.v-skeleton-loader__bone) {
  background: rgba(255, 255, 255, 0.04);
}

.dashboard-empty {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #8aa0aa;
  padding: 48px 16px;
  min-height: 280px;
}

.empty-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #cfd8dc;
  margin: 14px 0 4px;
}

.empty-subtitle {
  font-size: 0.9rem;
  margin: 0;
}
</style>
